import React from "react";

import {
  compact,
  filter,
  find,
  findIndex,
  isEmpty,
  isEqual,
  sortBy,
  toLower,
  uniqBy,
} from "lodash/fp";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import { EMPTY_TRANSCRIPT_SUMMARY } from "@app/analytics/api/models";
import {
  Edit,
  EditAccessLevel,
  NoteItemOrChapter,
  NoteEdit,
  NoteItemOrChapterType,
  RecapSection,
  TranscriptEdit,
  SingleTranscriptEdit,
  SingleNoteEdit,
  SpeakerEdit,
} from "@app/analytics/MeetingReport/types";
import {
  combineSingleEdits,
  combineTranscriptEdits,
  isBatchedEdit,
  isChapterEdit,
  isTurnEdit,
  replaceItemInList,
} from "@app/analytics/MeetingReport/utils/editUtils";
import type {
  SessionChapter,
  SessionNote,
  TranscriptSpeaker,
  TranscriptSummary,
  TranscriptSummaryItem,
  TranscriptTurnParagraph,
} from "@app/common/types";
import { insertAfter, insertAfterAll, replaceWhere } from "@app/utils/arrays";

type EditModal =
  | "confirm_cancel_changes"
  | "confirm_revert_notes"
  | "confirm_revert_transcript"
  | "confirm_delete_turn";

type EditAction = "save" | "undo" | "redo" | "deduped_save";

interface EditStateValues {
  modal: EditModal | null;
  /* which sections of the report the user can edit */
  editAccessLevel: EditAccessLevel;
  editingSection: RecapSection | null;
  notes: {
    activeItem: {
      id: NoteItemOrChapter["id"];
      type: NoteItemOrChapterType;
      subType?: "title" | "description"; // only applicable when editing chapters
    } | null;
    pendingEdit: NoteEdit | null;
    edits: NoteEdit[];
    editHistory: NoteEdit[];
    /* the edited version of the summary */
    summary: TranscriptSummaryItem;
    /* the edited version of the action items */
    actionItems: TranscriptSummaryItem[];
    /* the edited version of the key questions */
    keyQuestions: TranscriptSummaryItem[];
    /* the edited version of the key questions */
    chapters: SessionChapter[];
    /* the edited version of the topics */
    topics: TranscriptSummaryItem[];
    /* the edited version of the notes */
    notes: SessionNote[];
  };
  transcript: {
    activeParagraph: Pick<TranscriptTurnParagraph, "id"> | null;
    pendingEdit: TranscriptEdit | null;
    /* The current "saved" edits that have been made to the transcript */
    edits: TranscriptEdit[];
    /** History of edits that have been undone.
    /* Redo operations use this history to re-apply edits that were
    /* previously undone. History is cleared out when a new edit
    /* is saved **/
    editHistory: TranscriptEdit[];
    /* the edited version of the turns */
    turns: TranscriptTurnParagraph[];
    turnToDelete: TranscriptTurnParagraph | null;
    speakers: TranscriptSpeaker[];
  };
}

interface StartNoteEditSession {
  section: "notes";
  data: TranscriptSummary & {
    notes: SessionNote[];
  };
}

interface StartTranscriptEditSession {
  section: "transcript";
  data: {
    turns: TranscriptTurnParagraph[];
    speakers: TranscriptSpeaker[];
  };
}

type StartEditSessionOpts = StartNoteEditSession | StartTranscriptEditSession;

interface StartEditingTranscriptTurnOpts {
  type: "turn";
  item: TranscriptTurnParagraph;
}

interface StartEditingNoteOpts {
  type: NoteItemOrChapterType;
  item: NoteItemOrChapter;
  subType?: "title" | "description";
}

type StartEditingItemOpts =
  | StartEditingTranscriptTurnOpts
  | StartEditingNoteOpts;

type OnEditSpeakerClick = (
  event: React.MouseEvent<HTMLElement>,
  speakerName: string | null,
  turnId: string
) => void;

interface EditState extends EditStateValues {
  reset: () => void;
  setEditAccessLevel: (accessLevel: EditAccessLevel) => void;
  startEditSession: (sessionOpts: StartEditSessionOpts) => void;

  setModal: (modal: EditModal | null) => void;
  exitItem: () => void;
  startEditingItem: (opts: StartEditingItemOpts) => void;
  /* stores an edit for a specific item (transcript turn, note) */
  save: (edit: Edit) => void;
  undo: () => void;
  redo: () => void;

  /* Inserts a new turn and sets the new turn paragraph as the active paragraph */
  addTurn: (edit: TranscriptEdit) => void;
  /* Inserts a new paragraph onto an existing turn and sets the new paragraph as the active paragraph */
  addParagraph: (edit: TranscriptEdit) => void;
  /* Edits a speaker on a specific turn or the whole transcript */
  editSpeaker: (edit: SpeakerEdit) => void;
  /* stores a turn to be deleted */
  setTurnToDelete: (turnToDelete: TranscriptTurnParagraph) => void;
  /* deletes turn passed, or deletes a stored turn if there is one */
  deleteTurn: (turn: TranscriptTurnParagraph) => void;
  registerOnEditSpeakerClick: (onEditSpeakerClick: OnEditSpeakerClick) => void;
  onEditSpeakerClick: OnEditSpeakerClick;

  getItemsByType: (type: NoteItemOrChapterType) => NoteItemOrChapter[];
}

const DEFAULT_EDIT_STATE: EditStateValues = {
  modal: null,
  editAccessLevel: "none",
  editingSection: null,
  notes: {
    activeItem: null,
    edits: [],
    editHistory: [],
    ...EMPTY_TRANSCRIPT_SUMMARY,
    pendingEdit: null,
    notes: [],
  },
  transcript: {
    activeParagraph: null,
    turns: [],
    edits: [],
    editHistory: [],
    pendingEdit: null,
    turnToDelete: null,
    speakers: [],
  },
};

const handleFocusEditItem = (
  state: EditStateValues,
  opts: StartEditingItemOpts
) => {
  if (opts.type === "turn") {
    // cannot make a different turn active by clicking on another turn; has
    // to explicitly save or discard changes
    if (state.transcript.activeParagraph) return state;
    return {
      transcript: {
        ...state.transcript,
        activeParagraph: { id: opts.item.id },
      },
    };
  }

  // cannot make a different item active by clicking on another item; has
  // to explicitly save or discard changes
  if (state.notes.activeItem) return state;
  return {
    notes: {
      ...state.notes,
      activeItem: { id: opts.item.id, type: opts.type, subType: opts.subType },
    },
  };
};

const handleBlurEditItem = (state: EditStateValues) => {
  if (state.editingSection === "notes") {
    return { notes: { ...state.notes, activeItem: null } };
  }

  return {
    transcript: { ...state.transcript, activeParagraph: null },
  };
};

const handleStartEditing = (
  state: EditStateValues,
  { section, data }: StartEditSessionOpts
) => {
  let additionalChanges: Partial<EditStateValues> = {};

  // initialize the data into the store when starting an editing session
  if (section === "notes") {
    additionalChanges = {
      notes: { ...state.notes, ...data },
    };
  }

  if (section === "transcript") {
    additionalChanges = {
      transcript: { ...state.transcript, ...data },
    };
  }

  return { editingSection: section, ...additionalChanges };
};

const applySingleNoteEdit = (
  edit: SingleNoteEdit,
  noteState: EditStateValues["notes"],
  action: EditAction
): Partial<EditStateValues["notes"]> => {
  switch (edit.type) {
    case "summary":
      return { summary: edit.after };
    case "note":
      return {
        notes: replaceItemInList<SessionNote>({
          replacement: edit.after,
          itemToReplace: action === "deduped_save" ? edit.after : edit.before,
          list: noteState.notes,
        }),
      };
    case "action_item":
      return {
        actionItems: replaceItemInList<TranscriptSummaryItem>({
          replacement: edit.after,
          itemToReplace: action === "deduped_save" ? edit.after : edit.before,
          list: noteState.actionItems,
        }),
      };
    case "key_question":
      return {
        keyQuestions: replaceItemInList<TranscriptSummaryItem>({
          replacement: edit.after,
          itemToReplace: action === "deduped_save" ? edit.after : edit.before,
          list: noteState.keyQuestions,
        }),
      };
    case "topic":
      return {
        topics: replaceItemInList<TranscriptSummaryItem>({
          replacement: edit.after,
          itemToReplace: action === "deduped_save" ? edit.after : edit.before,
          list: noteState.topics,
        }),
      };
    case "chapter":
      return {
        chapters: replaceItemInList<SessionChapter>({
          replacement: edit.after,
          itemToReplace: edit.before,
          list: noteState.chapters,
        }),
      };
    default:
      return {};
  }
};

const shouldSaveAsPendingEdit = (edit: Edit): boolean => {
  if (isChapterEdit(edit)) return false;

  const edits = isBatchedEdit(edit) ? edit.edits : [edit];

  if (isTurnEdit(edits[0])) {
    const newTurnEdit = find((e) => !e.before, edits as SingleTranscriptEdit[]);
    // save edit as a pending edit if we're creating a new, empty turn/paragraph
    return newTurnEdit != null && !newTurnEdit.after.text;
  }

  // turn edit check is redundant but necessary to appease the types;
  // we don't do batched edits when creating a new summary item/session note,
  // so checking for a batched edit here is just to make the logic below simpler
  if (isTurnEdit(edit) || isBatchedEdit(edit)) {
    return false;
  }

  // save edit as a pending edit if we're creating a new, empty action item, key question, etc.
  return !edit.before && isEmpty(edit.after.value);
};

const shouldCombineEditWithPendingEdit = (
  edit: Edit,
  pendingEdit: Edit | null
): boolean => {
  if (!pendingEdit || isChapterEdit(edit) || isBatchedEdit(edit)) return false;

  const pendingEditEdits = isBatchedEdit(pendingEdit)
    ? pendingEdit.edits
    : [pendingEdit];

  if (isTurnEdit(pendingEditEdits[0])) {
    // we know the newTurnEdit exists in this set of edits because it had to have
    // for it to be saved as a pending edit in the first place
    const newTurnEdit = find(
      (e) => !e.before,
      pendingEditEdits as SingleTranscriptEdit[]
    )!;
    return edit.after.id === newTurnEdit.after.id;
  }

  // turn edit check is redundant but necessary to appease the types;
  // we don't do batched edits when creating a new summary item/session note,
  // so checking for a batched edit/pendingEdit here is just to make the
  // logic below simpler
  if (isTurnEdit(edit) || isBatchedEdit(pendingEdit)) {
    return false;
  }

  return (
    edit.type === pendingEdit.type && pendingEdit.after.id === edit.after.id
  );
};

const shouldDropEditAndPendingEdit = (edit: Edit, pendingEdit: Edit | null) => {
  if (!pendingEdit || isBatchedEdit(edit) || isChapterEdit(edit)) return false;

  const pendingEditEdits = isBatchedEdit(pendingEdit)
    ? pendingEdit.edits
    : [pendingEdit];

  if (isTurnEdit(pendingEditEdits[0])) {
    const newTurnEdit = find(
      (e) => !e.before,
      pendingEditEdits as SingleTranscriptEdit[]
    );
    if (!newTurnEdit) return false;
    return edit.after.id === newTurnEdit.after.id && edit.after.isDeleted;
  }

  // turn edit check is redundant but necessary to appease the types;
  // we don't do batched edits when creating a new summary item/session note,
  // so checking for a batched pendingEdit here is just to make the
  // logic below simpler
  if (isTurnEdit(edit) || isBatchedEdit(pendingEdit)) return false;

  // throw away the most recent edit and the pending edit if this was a situation
  // where the user created a new item and then saved it without text (the latter
  // action will be treated as a delete)
  return (
    edit.type === pendingEdit.type &&
    edit.after.isDeleted &&
    !pendingEdit.after.value
  );
};

const getUpdatedNoteEditsAndPendingEdit = (
  state: EditStateValues["notes"],
  edit: NoteEdit,
  initialAction: EditAction
): {
  state: Pick<EditStateValues["notes"], "pendingEdit" | "edits" | "activeItem">;
  action: EditAction;
  edit: NoteEdit;
} => {
  const pendingEdit = state.pendingEdit;
  if (pendingEdit == null && shouldSaveAsPendingEdit(edit)) {
    return {
      action: initialAction,
      edit,
      state: {
        activeItem: isBatchedEdit(edit)
          ? null
          : { type: edit.type, id: edit.after.id },
        pendingEdit: edit,
        edits: state.edits,
      },
    };
  }

  if (shouldDropEditAndPendingEdit(edit, pendingEdit)) {
    return {
      action: "undo",
      edit: pendingEdit!,
      state: {
        activeItem: null,
        pendingEdit: null,
        edits: state.edits,
      },
    };
  }

  if (shouldCombineEditWithPendingEdit(edit, pendingEdit)) {
    // typecast to appease the types. is safe to do because the condition
    // above will only return true if both `pendingEdit` and `edit` are
    // single edits of the same type
    const combinedEdit = combineSingleEdits(
      pendingEdit as SingleNoteEdit,
      edit as SingleNoteEdit
    );

    return {
      action: "deduped_save",
      edit: combinedEdit,
      state: {
        activeItem: null,
        pendingEdit: null,
        edits: [combinedEdit, ...state.edits],
      },
    };
  }

  return {
    action: initialAction,
    edit,
    state: {
      activeItem: null,
      pendingEdit: null,
      edits: [edit, ...state.edits],
    },
  };
};

const getUpdatedTranscriptEditsAndPendingEdit = (
  state: EditStateValues["transcript"],
  edit: TranscriptEdit,
  initialAction: EditAction
): {
  state: Pick<EditStateValues["transcript"], "pendingEdit" | "edits">;
  action: EditAction;
  edit: TranscriptEdit;
} => {
  const pendingEdit = state.pendingEdit;

  if (pendingEdit == null && shouldSaveAsPendingEdit(edit)) {
    return {
      action: initialAction,
      state: {
        pendingEdit: edit,
        edits: state.edits,
      },
      edit,
    };
  }

  if (shouldDropEditAndPendingEdit(edit, pendingEdit)) {
    const combinedEdit = combineTranscriptEdits(pendingEdit!, edit);
    return {
      action: "undo",
      state: {
        pendingEdit: null,
        edits: state.edits,
      },
      edit: combinedEdit,
    };
  }

  if (shouldCombineEditWithPendingEdit(edit, pendingEdit)) {
    const combinedEdit = combineTranscriptEdits(pendingEdit!, edit);

    return {
      action: "deduped_save",
      state: {
        pendingEdit: null,
        edits: [combinedEdit, ...state.edits],
      },
      edit: combinedEdit,
    };
  }

  return {
    action: initialAction,
    state: {
      pendingEdit: null,
      edits: [edit, ...state.edits],
    },
    edit,
  };
};

const applyNoteEdit = (
  edit: NoteEdit,
  noteState: EditStateValues["notes"],
  action: EditAction
): EditStateValues["notes"] => {
  const applyFn = action === "undo" ? undoSingleNoteEdit : applySingleNoteEdit;

  const createNoteEdit =
    action === "save"
      ? find((e) => !e.before, isBatchedEdit(edit) ? edit.edits : [edit])
      : undefined;

  if (isBatchedEdit(edit)) {
    let updatedNotesState = noteState;
    edit.edits.forEach((e) => {
      updatedNotesState = {
        ...updatedNotesState,
        ...applyFn(e, updatedNotesState, action),
      };
    });
    return {
      ...updatedNotesState,
      activeItem: createNoteEdit
        ? { type: createNoteEdit.type, id: createNoteEdit.after.id }
        : updatedNotesState.activeItem,
    };
  }

  return {
    ...noteState,
    ...applyFn(edit, noteState, action),
    activeItem: createNoteEdit
      ? { type: createNoteEdit.type, id: createNoteEdit.after.id }
      : noteState.activeItem,
  };
};

const applySingleTranscriptEdit = (
  edit: SingleTranscriptEdit,
  turns: EditStateValues["transcript"]["turns"],
  action: EditAction
): EditStateValues["transcript"]["turns"] => {
  if (edit.before) {
    return replaceWhere(turns, edit.after, isEqual(edit.before));
  }

  if (action === "deduped_save") {
    return replaceWhere(turns, edit.after, (p) => p.id === edit.after.id);
  }

  return insertAfterAll(
    turns,
    edit.after,
    (p) => p.turn.id === edit.after.turn.previousTurnId
  );
};

const applyTranscriptEdit = (
  edit: TranscriptEdit,
  turns: EditStateValues["transcript"]["turns"],
  action: EditAction
): EditStateValues["transcript"]["turns"] => {
  const applyFn =
    action === "undo" ? undoSingleTranscriptEdit : applySingleTranscriptEdit;
  if (isBatchedEdit(edit)) {
    let updatedTurns = turns;
    edit.edits.forEach((e) => {
      updatedTurns = applyFn(e, updatedTurns, action);
    });
    return updatedTurns;
  }

  return applyFn(edit, turns, action);
};

const insertParagraph = (
  edit: SingleTranscriptEdit,
  turns: EditStateValues["transcript"]["turns"]
) => {
  return insertAfter(
    turns,
    edit.after,
    (p) => p.id === edit.after.previousParagraphId
  );
};

const undoSingleTranscriptEdit = (
  edit: SingleTranscriptEdit,
  turns: EditStateValues["transcript"]["turns"]
): EditStateValues["transcript"]["turns"] => {
  return compact(
    replaceWhere(turns, edit.before, (p) => p?.id === edit.after.id)
  );
};

const undoSingleNoteEdit = (
  edit: SingleNoteEdit,
  noteState: EditStateValues["notes"]
): Partial<EditStateValues["notes"]> => {
  switch (edit.type) {
    case "summary":
      // there will always be a "before" for a summary value
      return { summary: edit.before! };
    case "note":
      return {
        notes: replaceItemInList<SessionNote>({
          replacement: edit.before,
          itemToReplace: edit.after,
          list: noteState.notes,
        }),
      };
    case "action_item":
      return {
        actionItems: replaceItemInList<TranscriptSummaryItem>({
          replacement: edit.before,
          itemToReplace: edit.after,
          list: noteState.actionItems,
        }),
      };
    case "key_question":
      return {
        keyQuestions: replaceItemInList<TranscriptSummaryItem>({
          replacement: edit.before,
          itemToReplace: edit.after,
          list: noteState.keyQuestions,
        }),
      };
    case "topic":
      return {
        topics: replaceItemInList<TranscriptSummaryItem>({
          replacement: edit.before,
          itemToReplace: edit.after,
          list: noteState.topics,
        }),
      };
    case "chapter":
      return {
        chapters: replaceItemInList<SessionChapter>({
          replacement: edit.before,
          itemToReplace: edit.after,
          list: noteState.chapters,
        }),
      };
    default:
      return {};
  }
};

const handleSaveEdit = (
  state: EditStateValues,
  edit: Edit
): Partial<EditStateValues> => {
  if (state.editingSection === "transcript") {
    const transcriptEdit = edit as TranscriptEdit;
    const {
      edit: editToApply,
      action,
      state: editState,
    } = getUpdatedTranscriptEditsAndPendingEdit(
      state.transcript,
      transcriptEdit,
      "save"
    );

    const updatedTurns = applyTranscriptEdit(
      editToApply,
      state.transcript.turns,
      action
    );

    return {
      transcript: {
        activeParagraph: null,
        editHistory: [], // clear the edit history (e.g. prevent "redo") when saving a new edit
        turns: updatedTurns,
        turnToDelete: null, // un-set turn to delete
        speakers: state.transcript.speakers,
        ...editState,
      },
    };
  }

  const noteEdit = edit as NoteEdit;
  const {
    edit: editToApply,
    state: editState,
    action,
  } = getUpdatedNoteEditsAndPendingEdit(state.notes, noteEdit, "save");
  const updatedNotesState = applyNoteEdit(editToApply, state.notes, action);

  return {
    notes: {
      ...updatedNotesState,
      ...editState,
      editHistory: [], // clear the edit history (e.g. prevent "redo") when saving a new edit
    },
  };
};

const handleUndoEdit = (state: EditStateValues): Partial<EditStateValues> => {
  if (state.editingSection === "transcript") {
    const [editToUndo, ...rest] = state.transcript.edits;
    const updatedTurns = applyTranscriptEdit(
      editToUndo,
      state.transcript.turns,
      "undo"
    );

    return {
      transcript: {
        ...state.transcript,
        activeParagraph: null,
        edits: rest,
        editHistory: [editToUndo, ...state.transcript.editHistory],
        turns: updatedTurns,
      },
    };
  }

  const [editToUndo, ...rest] = state.notes.edits;

  const updatedNotesState = applyNoteEdit(editToUndo, state.notes, "undo");

  return {
    notes: {
      ...updatedNotesState,
      activeItem: null,
      edits: rest,
      editHistory: [editToUndo, ...state.notes.editHistory],
    },
  };
};

const handleRedoEdit = (state: EditStateValues): Partial<EditStateValues> => {
  if (state.editingSection === "transcript") {
    const [editToRedo, ...rest] = state.transcript.editHistory;

    // UI should protect against us ever hitting this, but just in case
    if (!editToRedo) return state;

    const updatedTurns = applyTranscriptEdit(
      editToRedo,
      state.transcript.turns,
      "redo"
    );

    return {
      transcript: {
        ...state.transcript,
        edits: [editToRedo, ...state.transcript.edits],
        editHistory: rest,
        turns: updatedTurns,
      },
    };
  }

  const [editToRedo, ...rest] = state.notes.editHistory;

  // UI should protect against us ever hitting this, but just in case
  if (!editToRedo) return state;

  const updatedNotesState = applyNoteEdit(editToRedo, state.notes, "redo");

  return {
    notes: {
      ...updatedNotesState,
      activeItem: null,
      edits: [editToRedo, ...state.notes.edits],
      editHistory: rest,
    },
  };
};

const getSubsequentTurnAndParagraphEdits = (
  edit: SingleTranscriptEdit,
  turns: EditStateValues["transcript"]["turns"]
): SingleTranscriptEdit[] => {
  // 1. if there are any paragraphs following this new turn that were a part of
  // the previous turn, we need to migrate those to the new turn so the text
  // of the transcript doesn't get out of order.
  const paragraphsToMoveToNewTurn: TranscriptTurnParagraph[] = [];

  let nextParagraphIndex = edit.options?.nextParagraphId
    ? findIndex({ id: edit.options?.nextParagraphId }, turns)
    : -1;

  while (nextParagraphIndex > -1) {
    paragraphsToMoveToNewTurn.push(turns[nextParagraphIndex]);
    const nextParagraph = turns[nextParagraphIndex + 1];
    if (
      nextParagraph &&
      nextParagraph.turn.id === edit.after.turn.previousTurnId
    ) {
      nextParagraphIndex += 1;
    } else {
      nextParagraphIndex = -1;
    }
  }

  const paragraphEdits: SingleTranscriptEdit[] = paragraphsToMoveToNewTurn.map(
    (paragraph, i) => ({
      type: "turn",
      before: paragraph,
      after: {
        ...paragraph,
        previousParagraphId:
          paragraphsToMoveToNewTurn[i - 1]?.id ?? edit.after.id,
        turn: edit.after.turn,
      },
    })
  );

  // 2. since we are adding a new turn between existing turns, we need to update
  // the pointers on all paragraphs in the next turn to point to the new turn
  // as its previous turn
  const newTurnPreviousTurnId = edit.after.turn.previousTurnId;
  const newTurnId = edit.after.turn.id;
  const nextTurnParagraphs = filter(
    (t) => t.turn.previousTurnId === newTurnPreviousTurnId,
    turns
  );

  const previousTurnIdEdits: SingleTranscriptEdit[] = nextTurnParagraphs.map(
    (paragraph) => ({
      type: "turn",
      before: paragraph,
      after: {
        ...paragraph,
        turn: {
          ...paragraph.turn,
          previousTurnId: newTurnId,
        },
      },
    })
  );

  return [...paragraphEdits, ...previousTurnIdEdits];
};

const handleAddTurn = (
  state: EditStateValues,
  edit: TranscriptEdit
): Partial<EditStateValues> => {
  let updatedTurns = state.transcript.turns;

  const edits = isBatchedEdit(edit) ? edit.edits : [edit];
  const newTurnEdit = find((e) => !e.before, edits)!;
  const additionalEdits = getSubsequentTurnAndParagraphEdits(
    newTurnEdit,
    state.transcript.turns
  );

  const activeParagraphId = newTurnEdit.after.id;

  const batchedEdit: TranscriptEdit = {
    edits: [...additionalEdits, ...edits],
  };

  const { action, state: editState } = getUpdatedTranscriptEditsAndPendingEdit(
    state.transcript,
    batchedEdit,
    "save"
  );

  batchedEdit.edits.forEach((edit) => {
    updatedTurns = applySingleTranscriptEdit(edit, updatedTurns, action);
  });

  return {
    transcript: {
      activeParagraph: { id: activeParagraphId },
      editHistory: [], // clear the edit history (e.g. prevent "redo") when saving a new edit
      turns: updatedTurns,
      turnToDelete: null,
      speakers: state.transcript.speakers,
      ...editState,
    },
  };
};

const handleAddParagraph = (
  state: EditStateValues,
  edit: TranscriptEdit
): Partial<EditStateValues> => {
  let updatedTurns = state.transcript.turns;
  let newParagraphId = "";

  const { action, state: editState } = getUpdatedTranscriptEditsAndPendingEdit(
    state.transcript,
    edit,
    "save"
  );

  if (isBatchedEdit(edit)) {
    edit.edits.forEach((edit) => {
      if (edit.before) {
        updatedTurns = applySingleTranscriptEdit(edit, updatedTurns, action);
      } else {
        newParagraphId = edit.after.id;
        updatedTurns = insertParagraph(edit, updatedTurns);
      }
    });
  } else {
    newParagraphId = edit.after.id;
    // insert the new paragraph
    updatedTurns = insertAfter(
      state.transcript.turns,
      edit.after,
      (p) => p.id === edit.after.previousParagraphId
    );
  }

  return {
    transcript: {
      activeParagraph: { id: newParagraphId },
      editHistory: [], // clear the edit history (e.g. prevent "redo") when saving a new edit
      turns: updatedTurns,
      turnToDelete: null,
      speakers: state.transcript.speakers,
      ...editState,
    },
  };
};

const handleDeleteTurn = (
  state: EditState,
  turn: TranscriptTurnParagraph
): Partial<EditStateValues> => {
  const edit: TranscriptEdit = {
    type: "turn",
    before: turn,
    after: {
      ...turn,
      isDeleted: true,
    },
  };

  return handleSaveEdit(state, edit);
};

const handleEditSpeaker = (
  state: EditStateValues,
  edit: SpeakerEdit
): Partial<EditStateValues> => {
  let matchingTurns = [];
  const turnId = edit.after.turnId;

  if (turnId) {
    matchingTurns = filter(
      (item) => item.turn.id === turnId,
      state.transcript.turns
    );
  } else {
    matchingTurns = filter(
      (item) => item.turn.speaker === edit.before.speaker,
      state.transcript.turns
    );
  }

  if (isEmpty(matchingTurns)) {
    return state;
  }

  const edits: SingleTranscriptEdit[] = matchingTurns.map(
    (transcriptParagraphTurn) => ({
      type: "turn",
      before: { ...transcriptParagraphTurn },
      after: {
        ...transcriptParagraphTurn,
        turn: {
          ...transcriptParagraphTurn.turn,
          speaker: edit.after.speaker,
        },
      },
    })
  );

  const newState = handleSaveEdit(state, { edits });

  const newName = edit.after.speaker;

  if (!newName || !newState.transcript) return newState;

  const newSpeakers = uniqBy("name", [
    { name: newName },
    ...newState.transcript.speakers,
  ]);

  return {
    transcript: {
      ...newState.transcript,
      speakers: sortBy((speaker) => toLower(speaker.name ?? ""), newSpeakers),
    },
  };
};

const useEditStore = createWithEqualityFn<EditState>(
  (set, get) => ({
    ...DEFAULT_EDIT_STATE,
    reset: () =>
      set((state) => ({
        ...DEFAULT_EDIT_STATE,
        editAccessLevel: state.editAccessLevel,
      })),
    exitItem: () => set(handleBlurEditItem),
    setModal: (modal) => set({ modal }),
    setEditAccessLevel: (editAccessLevel) => set({ editAccessLevel }),
    startEditingItem: (opts) =>
      set((state) => handleFocusEditItem(state, opts)),
    startEditSession: (sessionOpts) =>
      set((state) => handleStartEditing(state, sessionOpts)),
    save: (edit) => set((state) => handleSaveEdit(state, edit)),
    undo: () => set(handleUndoEdit),
    redo: () => set(handleRedoEdit),
    registerOnEditSpeakerClick: (onEditSpeakerClick) =>
      set({ onEditSpeakerClick }),
    onEditSpeakerClick: () => {
      throw Error("must first call registerOnEditSpeakerClick with callback");
    },
    editSpeaker: (edit) => set((state) => handleEditSpeaker(state, edit)),
    addTurn: (edit) => set((state) => handleAddTurn(state, edit)),
    addParagraph: (edit) => set((state) => handleAddParagraph(state, edit)),
    setTurnToDelete: (turnToDelete) =>
      set((state) => ({ transcript: { ...state.transcript, turnToDelete } })),
    deleteTurn: (turn) => set((state) => handleDeleteTurn(state, turn)),
    getItemsByType: (type) => {
      switch (type) {
        case "action_item":
          return get().notes.actionItems;
        case "chapter":
          return get().notes.chapters;
        case "key_question":
          return get().notes.keyQuestions;
        case "note":
          return get().notes.notes;
        case "topic":
          return get().notes.topics;
        case "summary":
          return [get().notes.summary];
        default:
          return [];
      }
    },
  }),
  shallow
);

export default useEditStore;
